import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import type { ProblemDetailsWithCode } from "@/lib/problemDetails";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";

type CloneResult = components["schemas"]["MatchTestCloneResponse"];
type Population = components["schemas"]["RewindPopulation"];

// The generated schema widens int32 to `number | string`; counts are narrowed once here.
function toNumber(value: number | string | undefined): number {
  const parsed = typeof value === "string" ? Number(value) : value;
  return parsed === undefined || Number.isNaN(parsed) ? 0 : parsed;
}

// Date inputs are UTC calendar dates, as `new Date("2026-08-21")` parses them. The backend filters
// `CreatedAt <= to`, so createdTo needs endOfDayUtc or a same-day range selects nothing.
function startOfDayUtc(dateValue: string): string {
  return new Date(`${dateValue}T00:00:00.000Z`).toISOString();
}

function endOfDayUtc(dateValue: string): string {
  return new Date(`${dateValue}T23:59:59.999Z`).toISOString();
}

// AllBills by default: never-grouped bills are the misses a settings change is most likely to fix.
const POPULATIONS: { id: Population; title: string; blurb: string }[] = [
  {
    id: "AllBills",
    title: "All bills",
    blurb:
      "Every bill comes back for another pass, including ones the matcher never grouped because it " +
      "found no candidate. Those are the misses a settings change is most likely to fix, so this is " +
      "the default.",
  },
  {
    id: "GroupedOnly",
    title: "Only bills already matched",
    blurb:
      "Narrower: only bills the matcher already reached. Answers whether the matches you already make " +
      "get better, and cannot show you a miss being fixed.",
  },
];

type Mode = components["schemas"]["CloneMode"];

// Rewound stays first and default: it's what every clone did before, so flipping it would silently change the button.
const MODES: { id: Mode; title: string; blurb: string }[] = [
  {
    id: "Rewound",
    title: "Copy and re-run matching",
    blurb:
      "Copies the documents and re-runs matching straight away, so you see this client's bills matched " +
      "under the current settings. The matches it already has are not carried over - these are the " +
      "engine's, made fresh.",
  },
  {
    id: "Verbatim",
    title: "Copy as a baseline, don't run matching",
    blurb:
      "Copies everything as-is, including the matches this client already has, so you have something to " +
      "compare against. Nothing is re-matched: adjust AP settings on the clone, then use Re-run matching " +
      "on it.",
  },
];

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

interface CloneForMatchTestingDialogProps {
  clientId: string;
  clientName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCloned: () => void;
}

export function CloneForMatchTestingDialog({
  clientId,
  clientName,
  open,
  onOpenChange,
  onCloned,
}: CloneForMatchTestingDialogProps) {
  const [name, setName] = useState(() => `${clientName} (match test ${todayIso()})`);
  const [population, setPopulation] = useState<Population>("AllBills");
  const [mode, setMode] = useState<Mode>("Rewound");
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set only when `detail` names the orphaned clone: it holds real customer data and can't be deleted, so keep it findable.
  const [orphanedClientId, setOrphanedClientId] = useState<string | null>(null);
  // Set on any failure not provably pre-write: the copy commits before the rewind (R17), so a failed request
  // may still have created an undeletable clone. Blocks resubmission like a confirmed orphan.
  const [possibleOrphan, setPossibleOrphan] = useState(false);
  const [result, setResult] = useState<CloneResult | null>(null);

  function resetForm() {
    setName(`${clientName} (match test ${todayIso()})`);
    setPopulation("AllBills");
    setMode("Rewound");
    setCreatedFrom("");
    setCreatedTo("");
    setSubmitting(false);
    setError(null);
    setOrphanedClientId(null);
    setPossibleOrphan(false);
    setResult(null);
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) resetForm();
    onOpenChange(nextOpen);
  }

  // Re-enables the button only once the admin confirms they checked, not on any form keystroke.
  function clearOrphanBlock() {
    setOrphanedClientId(null);
    setPossibleOrphan(false);
    setError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    const { data, error: apiError } = await api.POST(
      "/admin/clients/{clientId}/clone-for-match-testing",
      {
        params: { path: { clientId } },
        body: {
          name: name || undefined,
          population,
          mode,
          createdFrom: createdFrom ? startOfDayUtc(createdFrom) : undefined,
          createdTo: createdTo ? endOfDayUtc(createdTo) : undefined,
        },
      },
    );

    setSubmitting(false);

    if (apiError || !data) {
      // Errors are problem+json with the code in `code` (see ProblemDetailsWithCode). Copy and rewind are separate
      // transactions (R17): if the rewind fails after the copy committed, `detail` names the orphaned clone.
      const problem = apiError as ProblemDetailsWithCode | undefined;
      const detail = problem?.detail;

      // Check `detail` before the code: a post-commit rewind failure returns its error code verbatim, which may have no branch here.
      const orphanMatch = detail?.match(/ClientId '([0-9a-f-]{36})'/i);
      const orphanId = orphanMatch?.[1];

      if (orphanId) {
        setOrphanedClientId(orphanId);
        setError(detail ?? "The clone was created but its rewind failed.");
        toast.warning("A clone was created but its rewind failed", {
          description: `ClientId ${orphanId}. It cannot be deleted - use Re-run matching on it.`,
        });
      } else if (problem?.code === "Clone.RewindScopeEmpty") {
        setError(
          detail
            ? `${detail} Widen the created-from / created-to dates and try again.`
            : "That window selects no bills. Widen the created-from / created-to dates and try again.",
        );
      } else if (problem?.code === "Clone.ScopeTooLarge") {
        setError(
          detail
            ? `${detail} Narrow the created-from / created-to window and try again.`
            : "That's too many bills for one synchronous run. Narrow the created-from / created-to " +
                "window and try again.",
        );
      } else if (problem?.code === "Clone.SourceClientNotFound") {
        setError(detail || "This client could not be found. It may have been removed.");
      } else {
        // The copy may already have committed, so never say "failed" as if nothing happened: a retry can mint a
        // second undeletable copy of real customer data.
        setPossibleOrphan(true);
        const message =
          (detail ? `${detail} ` : "") +
          "This may have failed after the copy step already committed, including on a timeout. Check " +
          `the client list for a new Sandbox client named after "${clientName}" before cloning again ` +
          "- there is no delete endpoint, so a duplicate cannot be removed.";
        setError(message);
        toast.warning("Cloning may have partly completed", { description: message });
      }
      return;
    }

    setResult(data);
    toast.success("Clone created", { description: `${data.name} (${data.clientId})` });
    // Refresh behind the dialog so the new clone is already listed when it closes.
    onCloned();
  }

  const blockedByPossibleOrphan = Boolean(orphanedClientId) || possibleOrphan;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        {result ? (
          <>
            <DialogHeader>
              <DialogTitle>Clone created</DialogTitle>
              <DialogDescription>
                <Link to={`/clients/${result.clientId}`} className="text-primary underline">
                  {result.name}
                </Link>{" "}
                is ready to inspect here in admin.
              </DialogDescription>
            </DialogHeader>

            <div>
              <div className="text-muted-foreground mb-1 text-xs font-medium">Copied</div>
              <div className="grid grid-cols-5 gap-2 text-center text-sm">
                <Stat label="Organisations" value={result.organisationIds.length} />
                <Stat label="Suppliers" value={toNumber(result.suppliersCopied)} />
                <Stat label="Bills" value={toNumber(result.billsCopied)} />
                <Stat label="Orders" value={toNumber(result.purchaseOrdersCopied)} />
                <Stat label="Deliveries" value={toNumber(result.deliveryNotesCopied)} />
              </div>
            </div>

            {result.rewind.matchingRan === false ? (
              // No counts block: all zeros would look like a clone that ran and matched nothing.
              <div className="space-y-1.5">
                <div className="text-muted-foreground text-xs font-medium">Matching</div>
                <p className="text-sm font-medium">Baseline copied - matching not run.</p>
                <p className="text-muted-foreground text-xs">
                  This clone carries the source's existing matches exactly as they were. Adjust its AP
                  settings, then use Re-run matching on the clone's page to produce a run to compare
                  against this baseline.
                </p>
              </div>
            ) : (
              <div>
                <div className="text-muted-foreground mb-1 text-xs font-medium">Rewind run</div>
                <div className="grid grid-cols-5 gap-2 text-center text-sm">
                  <Stat label="Match groups" value={toNumber(result.rewind.matchGroupsCreated)} />
                  <Stat label="Exact" value={toNumber(result.rewind.exactAllocations)} />
                  <Stat label="Fuzzy" value={toNumber(result.rewind.fuzzyAllocations)} />
                  <Stat label="Auto-accepted" value={toNumber(result.rewind.autoAccepted)} />
                  <Stat label="Unallocated" value={toNumber(result.rewind.linesUnallocated)} />
                </div>
              </div>
            )}

            <p className="text-amber-600 text-xs dark:text-amber-400">
              There is no delete. This clone stays around once created - remove it by hand if you need
              it gone.
            </p>

            <DialogFooter showCloseButton />
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>Clone for match testing</DialogTitle>
              <DialogDescription>
                Copy this client's matching data onto a fresh sandbox client with matching rewound, so
                auto-matching can be re-run under different AP settings.
              </DialogDescription>
            </DialogHeader>

            <div className="mt-4 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="clone-name">Name</Label>
                <Input
                  id="clone-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={`${clientName} (match test ${todayIso()})`}
                />
              </div>

              <div className="space-y-2">
                <Label>What to copy</Label>
                {MODES.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setMode(option.id)}
                    className={`w-full rounded-md border p-3 text-left transition ${
                      mode === option.id ? "border-primary bg-accent" : "hover:bg-accent/50"
                    }`}
                  >
                    <div className="text-sm font-medium">{option.title}</div>
                    <p className="text-muted-foreground mt-1 text-xs">{option.blurb}</p>
                  </button>
                ))}
                {mode === "Verbatim" && (
                  <p className="text-amber-600 text-xs dark:text-amber-400">
                    A baseline over 500 bills can't be re-run in one pass afterwards - only in date
                    windows, and separate windows aren't directly comparable with each other. There is no
                    delete, so narrow the dates below now if this client is large.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Which bills</Label>
                {POPULATIONS.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setPopulation(option.id)}
                    className={`w-full rounded-md border p-3 text-left transition ${
                      population === option.id ? "border-primary bg-accent" : "hover:bg-accent/50"
                    }`}
                  >
                    <div className="text-sm font-medium">{option.title}</div>
                    <p className="text-muted-foreground mt-1 text-xs">{option.blurb}</p>
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="clone-created-from">Created from</Label>
                  <Input
                    id="clone-created-from"
                    type="date"
                    value={createdFrom}
                    onChange={(e) => setCreatedFrom(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="clone-created-to">Created to</Label>
                  <Input
                    id="clone-created-to"
                    type="date"
                    value={createdTo}
                    onChange={(e) => setCreatedTo(e.target.value)}
                  />
                </div>
              </div>
              <p className="text-muted-foreground text-xs">
                Leave both blank to bring across every bill regardless of when it was created. Dates are
                read as UTC calendar days, and created-to includes the whole of that day. The window
                bounds what is copied, not just what is re-matched, so narrowing it makes the run smaller
                too. A single run is capped at 500 bills - a client over that fails immediately rather
                than copying partway, so narrow the window above if this client holds more.
              </p>

              <div className="border-amber-600/30 bg-amber-600/5 dark:border-amber-400/30 dark:bg-amber-400/5 space-y-1.5 rounded-md border p-3">
                <p className="text-amber-600 text-xs font-medium dark:text-amber-400">
                  Before you clone
                </p>
                <ul className="text-muted-foreground list-inside list-disc space-y-1 text-xs">
                  <li>
                    Copies organisations, suppliers, the bills in scope with their lines, purchase
                    orders and deliveries. Nothing else.
                  </li>
                  <li>
                    Bill, order and delivery lines are copied exactly as they were already extracted -
                    nothing is re-scanned - so matching settings are the only thing that changes between
                    the two clients.
                  </li>
                  <li>
                    No invoices, customers, webhooks, payment accounts, supplier bank details or
                    approval policies come across.
                  </li>
                  <li>
                    Bill PDFs are shared with this client, not duplicated - the copy can read them,
                    never delete them.
                  </li>
                  <li>
                    Emails, Gmail and pay-by-bank are off on the copy, and it gets fresh forwarding
                    addresses.
                  </li>
                  <li>The copy has no users and no API key. Inspect it here in admin.</li>
                  <li className="text-amber-600 dark:text-amber-400">
                    There is no delete. A clone cannot be removed once created.
                  </li>
                </ul>
              </div>

              {error && (
                <div className="space-y-2">
                  <p className="text-destructive text-sm">{error}</p>
                  {orphanedClientId && (
                    <div className="border-amber-600/30 bg-amber-600/5 dark:border-amber-400/30 dark:bg-amber-400/5 rounded-md border p-3">
                      <p className="text-amber-600 text-xs font-medium dark:text-amber-400">
                        A clone was created and still exists
                      </p>
                      <p className="text-muted-foreground mt-1 text-xs">
                        There is no delete endpoint, so it cannot be removed. Open{" "}
                        <Link to={`/clients/${orphanedClientId}`} className="text-primary underline">
                          this client
                        </Link>{" "}
                        and use Re-run matching there to finish the rewind.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <DialogFooter className="mt-6 flex-col items-stretch gap-2 sm:flex-col sm:items-stretch">
              {blockedByPossibleOrphan && (
                <Button type="button" variant="outline" size="sm" onClick={clearOrphanBlock}>
                  I checked the client list, no clone exists - allow cloning again
                </Button>
              )}
              <Button type="submit" disabled={submitting || blockedByPossibleOrphan}>
                {submitting && <Spinner className="size-4" />}
                Clone for match testing
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border p-2">
      <div className="text-lg font-semibold">{value.toLocaleString()}</div>
      <div className="text-muted-foreground text-xs">{label}</div>
    </div>
  );
}
